// The section's layout maths, apart from the DOM: the order of a slot's
// contributions, how many grid columns its cards take, and the insets that
// keep the header (and the content after the cards) within the cards.
// DashboardSection.tsx measures; these decide.

/** A slot's contributions by their `order`, registration order among equals. */
export const byOrder = <T extends { defaults?: { order?: number } }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0));

/**
 * How many grid columns a card takes, from its computed `grid-column-start`
 * and `grid-column-end` (`span 2` is two). A card spanning the whole row
 * (`1 / -1`, the wide cards) counts for none: it takes whatever width the
 * other cards set, never widens the section.
 */
export function columnsOf(gridColumnStart: string, gridColumnEnd: string): number {
  if (gridColumnStart === '1' && gridColumnEnd === '-1') return 0;
  return Number(/span (\d+)/.exec(`${gridColumnStart} ${gridColumnEnd}`)?.[1] ?? 1);
}

/**
 * The columns a section's cards take together (the CSS caps it). Zero when
 * every card spans the whole row (an empty line alone in a section): nothing
 * then narrows the section, which keeps its full capped width at the left
 * rather than one column's width in the middle (2026-10-13).
 */
export const sectionColumns = (cards: Array<{ start: string; end: string }>): number =>
  cards.reduce((n, c) => n + columnsOf(c.start, c.end), 0);

/**
 * The header's insets from the section's content box (`start`..`end`, in
 * viewport pixels) to where the first card starts and the widest row ends;
 * zero without cards, never negative.
 */
export function insets(content: { start: number; end: number }, boxes: Array<{ left: number; right: number }>): { start: number; end: number } {
  if (!boxes.length) return { start: 0, end: 0 };
  return {
    start: Math.max(0, Math.min(...boxes.map((b) => b.left)) - content.start),
    end: Math.max(0, content.end - Math.max(...boxes.map((b) => b.right))),
  };
}

/**
 * The used widths of a grid's columns, from its computed
 * `grid-template-columns` ("384px 384px 0px"): the tracks auto-fit collapsed
 * (no card in them, 0px) are dropped.
 */
export const tracksOf = (template: string): number[] =>
  template
    .split(/\s+/)
    .map((t) => /^(-?[\d.]+)px$/.exec(t))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => Number(m[1]))
    .filter((w) => w > 0);

/**
 * Which cards sit in the last row, given each card's top: the indices of the
 * lowest ones (a pixel of rounding is the same row). Empty when every card
 * sits in one row: a row that did not wrap is never moved.
 */
export function lastRowOf(tops: number[]): number[] {
  if (!tops.length) return [];
  const last = Math.max(...tops);
  const first = Math.min(...tops);
  if (last - first <= 1) return [];
  return tops.flatMap((t, i) => (last - t <= 1 ? [i] : []));
}

/** The columns one card of a row takes out of `columns`: a whole-row card (`1 / -1`) all of them. */
export const rowColumnsOf = (gridColumnStart: string, gridColumnEnd: string, columns: number): number =>
  gridColumnStart === '1' && gridColumnEnd === '-1' ? columns : Math.min(columns, columnsOf(gridColumnStart, gridColumnEnd));

/**
 * How far to move a wrapped last row that takes `used` of the grid's
 * `columns` (each `track` wide, `gap` apart) so it sits centered under the
 * rows above: half the room it leaves empty. Zero for a full row.
 */
export function lastRowShift(columns: number, used: number, track: number, gap: number): number {
  if (!(columns > 1) || !(used > 0) || used >= columns || !(track > 0)) return 0;
  return ((columns - used) * (track + Math.max(0, gap))) / 2;
}
